import { domainError, type DomainError } from '../shared/domain-error';
import { compareNames, normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { RoomId, UnitTypeId } from '../shared/ids';
import { roundQuantity } from '../schedule/unit-of-measure';
import { TAKEOFF_ERROR, TAKEOFF_ERROR_MESSAGE } from './errors';
import { checkItemFactors, type ItemFactor } from './own-factor';
import type { Point } from './polygon';
import { normalizeEdgesWithoutWall, type EdgeWithoutWall } from './room-edge';

/** Casas decimais de metro e metro quadrado — as mesmas do catálogo de unidades. */
export const GEOMETRY_DECIMALS = 2;

/**
 * O que o ambiente é.
 *
 * O tipo não é enfeite de tela: é dele que sai a única regra de conjugação (RN-161), e é
 * ele que deixa a área comum ser montada com os mesmos ambientes de sempre — escada e
 * circulação são ambientes como quarto e sala, com outro nome.
 */
export type RoomKind =
  | 'QUARTO'
  | 'SALA'
  | 'COZINHA'
  | 'BANHEIRO'
  | 'CLOSET'
  | 'AREA_SERVICO'
  | 'VARANDA'
  | 'CIRCULACAO'
  | 'ESCADA'
  | 'ELEVADOR'
  | 'AREA_EXTERNA'
  | 'OUTRO';

export const ROOM_KIND_LABEL: Readonly<Record<RoomKind, string>> = {
  QUARTO: 'Quarto',
  SALA: 'Sala',
  COZINHA: 'Cozinha',
  BANHEIRO: 'Banheiro',
  CLOSET: 'Closet',
  AREA_SERVICO: 'Área de serviço',
  VARANDA: 'Varanda',
  CIRCULACAO: 'Circulação',
  ESCADA: 'Escada',
  ELEVADOR: 'Área de elevador',
  AREA_EXTERNA: 'Área externa',
  OUTRO: 'Outro',
};

/**
 * Ambiente — o lugar onde o serviço acontece (RN-157).
 *
 * Pertence a uma **tipologia**, não a um apto: o quantitativo mora na tipologia e o apto
 * herda (RN-167, RN-177). Guarda largura e comprimento porque é isso que alguém consegue
 * tirar da planta; área e perímetro saem daí — e podem ser **corrigidos por cima**, para
 * a sala em L, que não é retângulo e não vai virar dois ambientes só para caber na conta.
 */
export interface Room {
  readonly id: RoomId;
  readonly unitTypeId: UnitTypeId;
  readonly name: string;
  readonly code?: string;
  readonly kind: RoomKind;
  /** Metros. */
  readonly width: number;
  readonly length: number;
  /**
   * Pé-direito do ambiente, em metros — o que vale para revestimento, forro e pintura.
   *
   * **Não** é a altura da parede (RN-162). Forro rebaixado a 2,50 não encurta a alvenaria
   * de 2,90; encurta a pintura. São dois números porque são duas coisas.
   */
  readonly ceilingHeight: number;
  /**
   * O contorno traçado sobre a planta, em metros.
   *
   * Quando existe, é ele quem manda: área e perímetro saem do polígono, para qualquer
   * forma. Largura e comprimento continuam guardados como o retângulo aproximado — servem
   * de resumo na lista e de rede de segurança para o ambiente que ainda não foi traçado.
   *
   * As coordenadas são da **tipologia**, não do ambiente: é o que permite descobrir a
   * divisa entre dois ambientes por geometria, em vez de pedir que alguém a aponte.
   */
  readonly outline?: readonly Point[];
  /**
   * Os lados do contorno que **não** são parede — guarda-corpo e aberto (Fase 24, D25).
   *
   * Mora no ambiente, ao lado do contorno, porque o índice só quer dizer alguma coisa contra
   * **este** contorno (ADR-149): redesenhar o ambiente muda os lados, e os dois têm de mudar
   * juntos. Ausente é o caso comum — todo lado é parede —, e é assim que todo ambiente de
   * antes desta fatia continua contando.
   */
  readonly edgesWithoutWall?: readonly EdgeWithoutWall[];
  /** Correção da área calculada. Ausente = vale o contorno, ou largura × comprimento. */
  readonly areaOverride?: number;
  /** Correção do perímetro calculado. Ausente = vale 2 × (largura + comprimento). */
  readonly perimeterOverride?: number;
  /**
   * O fator próprio de cada item neste cômodo, nos serviços que descem a cômodo, por cima do
   * índice do item (Fase 39, D14, RN-512) — "o banheiro leva 6 kg de argamassa por m² de piso".
   * Ausente ou vazio, todo item segue o índice. Não pede motivo (RN-513).
   */
  readonly factors?: readonly ItemFactor[];
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface CreateRoomInput {
  readonly id: RoomId;
  readonly unitTypeId: UnitTypeId;
  readonly name: string;
  readonly kind: RoomKind;
  readonly width: number;
  readonly length: number;
  readonly ceilingHeight: number;
  readonly code?: string;
  readonly outline?: readonly Point[];
  readonly edgesWithoutWall?: readonly EdgeWithoutWall[];
  readonly areaOverride?: number;
  readonly perimeterOverride?: number;
  /** O fator próprio de cada item (Fase 39, RN-512), conferido aqui (`checkItemFactors`). */
  readonly factors?: readonly ItemFactor[];
  readonly notes?: string;
  readonly now: string;
}

function positive(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

export function createRoom(input: CreateRoomInput): Result<Room, DomainError> {
  const name = input.name.trim();
  if (name.length === 0) {
    return err(
      domainError(
        TAKEOFF_ERROR.ROOM_NAME_REQUIRED,
        TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.ROOM_NAME_REQUIRED]!,
      ),
    );
  }
  if (!positive(input.width) || !positive(input.length)) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_DIMENSION,
        TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.INVALID_DIMENSION]!,
        { width: input.width, length: input.length },
      ),
    );
  }
  if (!positive(input.ceilingHeight)) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_HEIGHT,
        TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.INVALID_HEIGHT]!,
        { ceilingHeight: input.ceilingHeight },
      ),
    );
  }
  if (input.areaOverride !== undefined && !positive(input.areaOverride)) {
    return err(
      domainError(TAKEOFF_ERROR.INVALID_DIMENSION, 'A área corrigida deve ser maior que zero.', {
        areaOverride: input.areaOverride,
      }),
    );
  }
  if (input.perimeterOverride !== undefined && !positive(input.perimeterOverride)) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_DIMENSION,
        'O perímetro corrigido deve ser maior que zero.',
        { perimeterOverride: input.perimeterOverride },
      ),
    );
  }
  // O guarda-corpo digitado com altura zero ou negativa é recusado, e não trocado pela de
  // padrão: quem digitou quis um número, e a área que se compra sai dele (D25).
  const guardaCorpoSemAltura = input.edgesWithoutWall?.find(
    (lado) => lado.kind === 'GUARDA_CORPO' && !positive(lado.height),
  );
  if (guardaCorpoSemAltura !== undefined) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_HEIGHT,
        TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.INVALID_HEIGHT]!,
        { edgeIndex: guardaCorpoSemAltura.edgeIndex },
      ),
    );
  }
  const edgesWithoutWall = normalizeEdgesWithoutWall(input.edgesWithoutWall);
  // O fator próprio (Fase 39, RN-512): maior que zero, um por item. A lista vazia fica — é ela
  // que diz "nenhum" a quem grava.
  const fatores = input.factors === undefined ? undefined : checkItemFactors(input.factors);
  if (fatores !== undefined && !fatores.ok) return err(fatores.error);

  return ok({
    id: input.id,
    unitTypeId: input.unitTypeId,
    name,
    kind: input.kind,
    width: input.width,
    length: input.length,
    ceilingHeight: input.ceilingHeight,
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.code === undefined ? {} : { code: input.code }),
    ...(input.outline === undefined ? {} : { outline: input.outline }),
    ...(edgesWithoutWall === undefined ? {} : { edgesWithoutWall }),
    ...(input.areaOverride === undefined ? {} : { areaOverride: input.areaOverride }),
    ...(input.perimeterOverride === undefined
      ? {}
      : { perimeterOverride: input.perimeterOverride }),
    ...(fatores === undefined ? {} : { factors: fatores.value }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  });
}

/** Área de piso — e de teto, que é a mesma (RN-157). */
export function roomArea(room: Pick<Room, 'width' | 'length' | 'areaOverride'>): number {
  return roundQuantity(room.areaOverride ?? room.width * room.length, GEOMETRY_DECIMALS);
}

export function roomPerimeter(room: Pick<Room, 'width' | 'length' | 'perimeterOverride'>): number {
  return roundQuantity(room.perimeterOverride ?? 2 * (room.width + room.length), GEOMETRY_DECIMALS);
}

/** A correção foi usada? A tela mostra, para que o número editado não passe por calculado. */
export function hasCorrectedGeometry(
  room: Pick<Room, 'areaOverride' | 'perimeterOverride'>,
): boolean {
  return room.areaOverride !== undefined || room.perimeterOverride !== undefined;
}

/**
 * Este ambiente aceita ser conjugado? (RN-161)
 *
 * Conjugar é **remover a parede** entre dois ambientes. Banheiro não conjuga porque
 * sempre há parede ali — não é preferência de projeto, é o que a obra faz.
 */
export function canBeJoined(kind: RoomKind): boolean {
  return kind !== 'BANHEIRO';
}

export function bathroomCannotBeJoinedError(): DomainError {
  return domainError(
    TAKEOFF_ERROR.BATHROOM_CANNOT_BE_JOINED,
    TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.BATHROOM_CANNOT_BE_JOINED]!,
  );
}

/** Chave natural do ambiente dentro da tipologia. */
export function roomNaturalKey(unitTypeId: UnitTypeId, name: string): string {
  return `${unitTypeId}|${normalizeName(name)}`;
}

export function compareRooms(a: Room, b: Room): number {
  return compareNames(a.name, b.name);
}
